import { combineLatest, Observable, of } from 'rxjs'
import { AppState } from 'src/app/core/core.state'

import { Component, OnInit, ViewChild } from '@angular/core'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { courseActions } from '../course.actions'
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  take,
  tap,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { NotificationService } from 'src/app/core/notification.service'
import { Course } from '../course'
import { UntypedFormGroup } from '@angular/forms'
import { CourseFormComponent } from '../form-general/form-general.component'
import { ActivatedRoute, Router } from '@angular/router'
import { courseSelectors } from '../course.selectors'
import { UntilDestroy } from '@ngneat/until-destroy'
import { productSelectors } from '../../product/product.selectors'

@UntilDestroy()
@Component({
  selector: 'course-create',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class CourseCreateComponent implements OnInit {
  @ViewChild(CourseFormComponent, { static: false })
  private generalCourseFormRef: CourseFormComponent

  public courseId$: Observable<number>
  public course$: Observable<Course>

  public form: UntypedFormGroup
  public formGeneral: Partial<Course>

  private didFetchCourseId: boolean = false

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private notificationService: NotificationService,
    private router: Router,
    private activeRoute: ActivatedRoute
  ) {}

  ngOnInit() {
    this.courseId$ = this.activeRoute.params.pipe(
      map((params) =>
        params.hasOwnProperty('courseId') ? Number(params['courseId']) : null
      ),
      distinctUntilChanged(),
      tap((courseId) => {
        if (courseId && !this.didFetchCourseId) {
          this.store.dispatch(courseActions.fetchOneById({ id: courseId }))
          this.didFetchCourseId = true
        }
      })
    )

    this.course$ = combineLatest([
      this.courseId$,
      this.store.select(courseSelectors.selectEntities)
    ]).pipe(map(([courseId, courseEntitities]) => courseEntitities[courseId]))
  }

  public onClickCreate() {
    this.generalCourseFormRef.attemptSubmit()
  }

  public onSubmitGeneralForm(course: Partial<Course>) {
    this.course$
      .pipe(
        take(1),
        withLatestFrom(this.store.select(productSelectors.selectEntities))
      )
      .subscribe(([existingCourse, productEntities]) => {
        if (existingCourse && existingCourse.id) {
          this.store.dispatch(
            courseActions.updateOneById({
              id: +existingCourse.id,
              params: {
                ...course,
                id: existingCourse.id
              }
            })
          )
        } else {
          this.store.dispatch(
            courseActions.createOne({
              entity: {
                ...course,
                siteId: productEntities[course.productId]?.siteId
              }
            })
          )
        }
        // await for the course to be created/updated and navigate to bookings tab
        this.actions$
          .pipe(
            ofType(courseActions.upsertMany),
            take(1),
            filter((action) => Boolean(action.entities?.length)),
            map((action) => action.entities[0]),
            timeout(1000),
            catchError(() => of(null))
          )
          .subscribe((course: Course) => {
            if (!course) {
              return this.notificationService.emit('alerts.error', 'error')
            }

            this.router.navigate([`/courses/${course.id}/bookings/create`])

            this.notificationService.emit('alerts.success', 'success')
          })
      })
  }
}
